import { useEffect, useState } from "react";

/**
 * Reusable hook for fetching API data with date range and loading/error state.
 * @param apiPath The API endpoint to call
 * @param dateRange The date range object with from/to
 * @param options Optional: transform function to process the API response
 */
export function useApiDataWithDateRange<T = any>(
  apiPath: string,
  dateRange: { from?: Date; to?: Date } | undefined,
  options?: {
    transform?: (res: any) => T;
    requestFn?: (path: string, params: any) => Promise<any>;
  }
): [T, boolean, string | null] {
  const [data, setData] = useState<T>(
    options?.transform ? options.transform(undefined) : (undefined as any)
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchStats() {
      setLoading(true);
      setError(null);
      if (!dateRange?.from || !dateRange?.to) {
        setLoading(false);
        return;
      }
      try {
        const reqFn =
          options?.requestFn ||
          (await import("@helpers/requestSuperOneApiData"))
            .requestSuperOneApiData;
        const res = await reqFn(apiPath, {
          from: dateRange.from,
          to: dateRange.to,
        });
        setData(options?.transform ? options.transform(res) : res);
      } catch (e: any) {
        setError(e.message || "Failed to fetch stats");
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, apiPath]);

  return [data, loading, error];
}
